import React, { useEffect } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import { ROUTES } from './routes';

const JOURNEYBOT_CHATGPT_URL = 'https://chatgpt.com/g/g-6a543064ab5081918b030dc9cddf51ea-journeyapp';

const workflow = [
  'Open JourneyBot',
  'Discuss your goal',
  'Refine the plan',
  'Save the journey',
  'Continue chatting',
  'Return through ChatGPT history',
];

const commands = [
  { title: 'Create or save', items: ['Save this journey.', 'Create a new journey from this conversation.', 'Save this as “First Home Plan”.'] },
  { title: 'Update', items: ['Add this as a new action.', 'Update the journey with what we discussed.', 'Change the goal to…'] },
  { title: 'Progress', items: ['Mark this action complete.', 'Defer this until next month.', 'What is my next action?'] },
  { title: 'Review', items: ['Show me my current journey.', 'Summarise my progress.', 'What is blocking me?'] },
];

const faqs = [
  {
    question: 'I cannot find JourneyBot',
    answer: 'In ChatGPT, open Explore GPTs or Custom GPTs and search for “JourneyBot”. Check that you are signed in to the ChatGPT account you normally use.',
  },
  {
    question: 'JourneyBot asks me to sign in',
    answer: 'You may need to sign in to your JourneyBot account so that saved journeys belong to you. Follow the sign-in steps shown and do not use unsupported workarounds.',
  },
  {
    question: 'I discussed a journey but cannot see it in the app',
    answer: 'Check whether you explicitly asked JourneyBot to save the journey and whether the save action reported success. A discussion by itself does not necessarily create a saved journey.',
  },
  {
    question: 'I cannot find my previous conversation',
    answer: 'The conversation should normally be in ChatGPT history unless it was deleted, history was disabled, or you used a different ChatGPT account. ChatGPT behaviour may change over time.',
  },
  {
    question: 'My saved journey and chat conversation look different',
    answer: 'The chat may contain exploratory discussion, while the saved journey contains the structured goal, steps, and actions you asked JourneyBot to store.',
  },
];

function ExampleList({ items }) {
  return (
    <Box component="ul" sx={{ pl: 3, my: 1 }}>
      {items.map((item) => <Typography component="li" variant="body1" key={item} sx={{ mb: 0.75 }}>{item}</Typography>)}
    </Box>
  );
}

function Step({ number, title, children }) {
  return (
    <Card component="section" variant="outlined" data-testid="primary-step" sx={{ scrollMarginTop: 80 }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Chip label={number} color="primary" aria-hidden="true" sx={{ fontWeight: 800, mt: 0.25 }} />
          <Box sx={{ minWidth: 0, maxWidth: 760 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 850, mb: 1.5 }}>{title}</Typography>
            {children}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function HowToUse() {
  useEffect(() => {
    document.title = 'Use JourneyBot with ChatGPT Plus | JourneyBot';
    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement('meta');
      description.setAttribute('name', 'description');
      document.head.appendChild(description);
    }
    description.setAttribute('content', 'Learn how to use JourneyBot in ChatGPT to discuss a goal, create a structured journey, save it and return to continue your progress.');
  }, []);

  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary', textAlign: 'left', py: { xs: 4, md: 7 } }}>
      <Container maxWidth="md">
        <Stack spacing={{ xs: 4, md: 6 }}>
          <Box component="header" sx={{ maxWidth: 780 }}>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>Ways to Use</Typography>
            <Typography variant="h2" component="h1" sx={{ fontWeight: 900, mt: 0.5 }}>Use with ChatGPT Plus</Typography>
            <Typography variant="h6" component="p" color="text.secondary" sx={{ mt: 2, maxWidth: 720, lineHeight: 1.6 }}>
              JourneyBot helps turn a ChatGPT conversation into an organised journey that you can save, revisit and continue over time.
            </Typography>
            <Typography sx={{ mt: 2 }}>
              Prefer to manage your journey directly in your browser? See{' '}
              <Link component={RouterLink} to={ROUTES.waysToUseWebApp}>Use the Web App</Link>.
            </Typography>
          </Box>

          <Box component="section" aria-labelledby="workflow-heading">
            <Typography id="workflow-heading" variant="h4" component="h2" sx={{ fontWeight: 900, mb: 2.5 }}>The JourneyBot workflow</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1, md: 0 }} alignItems="stretch">
              {workflow.map((item, index) => (
                <React.Fragment key={item}>
                  <Card variant="outlined" sx={{ flex: 1, display: 'flex' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{item}</Typography>
                    </CardContent>
                  </Card>
                  {index < workflow.length - 1 && <ArrowForwardIcon color="action" aria-hidden="true" sx={{ alignSelf: 'center', transform: { xs: 'rotate(90deg)', md: 'none' }, mx: { md: 0.5 } }} />}
                </React.Fragment>
              ))}
            </Stack>
          </Box>

          <Stack spacing={2.5}>
            <Step number="1" title="Open JourneyBot in ChatGPT">
              <Typography>Open ChatGPT, go to Explore GPTs or Custom GPTs, search for JourneyBot, then open it and begin a new conversation.</Typography>
              <Button component="a" href={JOURNEYBOT_CHATGPT_URL} target="_blank" rel="noopener noreferrer" variant="contained" endIcon={<OpenInNewIcon />} sx={{ mt: 2 }}>Open JourneyBot in ChatGPT</Button>
            </Step>

            <Step number="2" title="Talk about what you want to achieve">
              <Typography>Describe your goal in normal conversational language. For example:</Typography>
              <ExampleList items={['I want to improve my fitness.', 'Help me plan my first home purchase.', 'I want to create a roadmap for my business idea.', 'Help me organise my career change.']} />
              <Typography>JourneyBot can help identify your intended outcome, current position, important steps, useful actions, and blockers or uncertainties. You can explore an idea without saving it straight away.</Typography>
            </Step>

            <Step number="3" title="Refine the journey through conversation">
              <Typography>Keep chatting until the journey feels useful. You do not need to complete a form before discussing it.</Typography>
              <ExampleList items={['Break this into smaller steps.', 'What should I do first?', 'Add a step for researching my options.', 'That action is not relevant to me.', 'Make the plan more realistic.']} />
            </Step>

            <Step number="4" title="Save the journey">
              <Typography>When you are ready, explicitly ask JourneyBot to save it:</Typography>
              <ExampleList items={['Save this journey.', 'Save this to JourneyBot.', 'Create a journey from this conversation.', 'Store this as a new journey.']} />
              <Alert severity="info" sx={{ mt: 2 }}><strong>Your journey is saved when you explicitly ask JourneyBot to save it and approve any required confirmation.</strong> Merely discussing a plan does not necessarily save it. Treat it as saved only when the JourneyBot action confirms success.</Alert>
            </Step>

            <Step number="5" title="Continue the conversation">
              <Typography>After saving, you can continue in the same ChatGPT conversation:</Typography>
              <ExampleList items={['What should I work on next?', 'Add another action to the first step.', 'Mark that action as complete.', 'Defer this task until next month.', 'Update the journey with what we just discussed.']} />
              <Typography>Some changes may require another explicit save or update command. Do not assume every chat message automatically changes the stored journey.</Typography>
            </Step>

            <Step number="6" title="Leave and return later">
              <Typography>Leave ChatGPT when you are finished. Later, open ChatGPT, find the previous JourneyBot conversation in your chat history, reopen it, and continue discussing or updating your journey.</Typography>
              <Alert severity="info" sx={{ mt: 2 }}>The ChatGPT conversation contains the discussion. JourneyBot stores the structured journey after you ask it to save. JourneyBot does not automatically reopen the correct conversation for you.</Alert>
              {/* Future real screenshots could show Explore GPTs, starting a conversation, saving, and returning through ChatGPT history. */}
            </Step>
          </Stack>

          <Box component="section" aria-labelledby="commands-heading">
            <Typography id="commands-heading" variant="h4" component="h2" sx={{ fontWeight: 900 }}>Useful commands</Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 2.5 }}>These are conversational examples, not guaranteed exact command syntax.</Typography>
            <Grid container spacing={2}>
              {commands.map((group) => (
                <Grid item xs={12} sm={6} key={group.title}>
                  <Card variant="outlined" sx={{ height: '100%' }}><CardContent><Typography variant="h6" component="h3" sx={{ fontWeight: 800 }}>{group.title}</Typography><ExampleList items={group.items} /></CardContent></Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box component="section" aria-labelledby="troubleshooting-heading">
            <Typography id="troubleshooting-heading" variant="h4" component="h2" sx={{ fontWeight: 900, mb: 2.5 }}>Troubleshooting</Typography>
            {faqs.map(({ question, answer }) => (
              <Accordion key={question} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${question}-content`}><Typography component="h3" sx={{ fontWeight: 800 }}>{question}</Typography></AccordionSummary>
                <AccordionDetails><Typography color="text.secondary">{answer}</Typography></AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
